import { useEffect, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet.jsx';
import { formatDateTime } from '@/lib/date-display.js';
import { getV3Message, FIXTURE_BADGE } from '@/services/v3.service.js';
import FactLine from './FactLine.jsx';
import { NOT_CONNECTED } from '@/services/v3.service.js';
import { MAIL_NOT_CONNECTED, CHANNEL_WORDS, CONVERSATION_ERROR_WORDS, DIRECTION_WORDS, EARLIER_NOT_LOADED, LIKELY_AUTOMATIC, MATCH_WORDS, MATCHED_BY_WORDS, MESSAGE_STATUS_CLASS, MESSAGE_STATUS_WORDS, NOT_READ_YET, NOT_RECORDED, NOT_SENT_YET, NO_MESSAGES, UNATTRIBUTED_MESSAGES, word } from './words.js';

// THE CONVERSATION (stage D3, Track A's docs/v3-mail-api.md, 5 and 6): one
// list per account, read once by the page through v3.service
// getV3Communications and handed down as `conversation` ({ data, source,
// error, loading, reload }). The payload is already folded (by policy_id,
// then crm_deal_id, else unattributed; never guessed onto the current term)
// and already ordered newest first; this file renders that fold and derives
// nothing. Email, SMS, outbound and inbound are one list: a row carries its
// direction and channel as words. The BADGE is message.status as stored,
// through MESSAGE_STATUS_WORDS (the contract's eight; opened and clicked are
// not among them). Opens and clicks are SIGNALS, rendered lighter beside the
// badge, and "likely automatic" is the view's own flag, printed only when it
// is true. The drill-in (MessageSheet) reads GET /api/v3/messages/:id and
// shows the message as sent: the HTML in a sandboxed iframe (no script, no
// same origin), else the text. Its events list is a list; it never becomes
// the badge. TermCommunications is the fold TermBlock renders under one term.

function counterparty(m) {
  if (m.direction === 'inbound') return m.from || NOT_RECORDED;
  const to = Array.isArray(m.to) ? m.to : [];
  return to.length ? to.join(', ') : NOT_RECORDED;
}
function previewOf(m) {
  const p = m.preview || '';
  return p.length >= 140 ? `${p}...` : p;
}

export function StatusBadge({ status }) {
  return <span className={`inline-flex shrink-0 items-center rounded-pill px-2 py-0.5 text-[11px] font-medium leading-none ${MESSAGE_STATUS_CLASS[status] || 'bg-border/60 text-text'}`} data-status-badge data-message-status={status ?? 'not_recorded'}>{word(MESSAGE_STATUS_WORDS, status)}</span>;
}

// The signals, lighter than the badge and never the badge: the last open
// (with the view's "likely automatic" when it says true) and the clicks.
export function Signals({ signals }) {
  const s = signals || {};
  const open = s.last_open || null;
  const clicks = Number(s.clicks || 0);
  if (!open && !clicks) return null;
  return (
    <span className="text-[11px] text-text-dim" data-signals>
      {open ? <span data-last-open>opened {formatDateTime(open.at)}{open.likely_automatic === true ? <span data-likely-automatic>, {LIKELY_AUTOMATIC}</span> : null}</span> : null}
      {open && clicks ? '; ' : ''}
      {clicks ? <span data-clicks>{clicks} {clicks === 1 ? 'click' : 'clicks'}</span> : null}
    </span>
  );
}

export function MessageRow({ m, onOpen }) {
  const main = m.subject || previewOf(m) || NOT_RECORDED;
  return (
    <button type="button" onClick={() => onOpen(m.id)} className="flex min-h-[44px] w-full flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-border py-2 text-left last:border-b-0 hover:bg-border/20" data-message={m.id} data-direction={m.direction} data-channel={m.channel}>
      <span className="font-mono text-xs text-text-muted">{m.at ? formatDateTime(m.at) : NOT_SENT_YET}</span>
      <span className="text-[11px] uppercase tracking-wide text-text-dim" data-direction-word>{word(DIRECTION_WORDS, m.direction)}</span>
      <span className="text-[11px] text-text-dim" data-channel-word>{word(CHANNEL_WORDS, m.channel)}</span>
      <span className="text-sm font-medium text-text">{main}</span>
      <span className="text-xs text-text-dim">{m.direction === 'inbound' ? 'from' : 'to'} {counterparty(m)}</span>
      <StatusBadge status={m.status} />
      <Signals signals={m.signals} />
      {Array.isArray(m.attachments) && m.attachments.length ? <span className="inline-flex items-center gap-1 text-[11px] text-text-dim" data-attachments><Paperclip size={11} strokeWidth={1.5} /> {m.attachments.length}</span> : null}
      {m.match ? <span className="text-[11px] text-text-dim" data-match={m.match.status}>{word(MATCH_WORDS, m.match.status)}{m.match.by ? ` ${word(MATCHED_BY_WORDS, m.match.by)}` : ''}</span> : null}
      {m.subject && m.preview ? <span className="basis-full text-xs text-text-muted">{previewOf(m)}</span> : null}
    </button>
  );
}

// THE DRILL-IN: the message as sent or received. body_html renders in an
// iframe with an empty sandbox (no script runs, no origin is shared, nothing
// in it can reach this page); a message without HTML shows its text. The
// events are listed oldest first as the server orders them.
// INBOUND HTML LOADS NOTHING FROM THE NETWORK. An empty sandbox blocks
// scripts, forms and same-origin access, and no-referrer hides the referrer,
// but neither stops a tracking pixel or a remote image from loading the moment
// the broker opens the drill-in, which tells the sender the message was read
// and from where: the same signal this page reads as "opened" on outbound
// mail, manufactured here for a third party. So the document gets a CSP that
// allows no network source at all: inline styles and data: images and fonts
// only. ALWAYS THE FIRST BYTES OF THE DOCUMENT, never placed after a <head>
// the string happens to contain: a "<head>" inside a comment, or an <img>
// sitting before the real head, would otherwise load before the policy is
// parsed. The parser implies html and head around a leading meta, so the
// policy is in the head and enforced from the first byte. A message that
// carries its own doctype keeps standards mode: its doctype is stripped and
// "<!doctype html>" is emitted ahead of the meta, so the document holds one
// doctype, then the policy, then the message. Review fix, 2026-10-03;
// prefix-always and the doctype in the follow-up the same night.
// THREE NAVIGATIONS THE CSP ALONE DOES NOT STOP (closed 2026-10-03, overnight
// 2). No sandbox token stops a frame navigating ITSELF, so an empty sandbox
// leaves three doors: <meta http-equiv="refresh"> (CSP never governed it;
// navigate-to never shipped), <base href> rewriting every relative URL in the
// message, and <form action> submission. The last two CSP does govern, so the
// policy carries form-action 'none' and base-uri 'none'. The first it does
// not, so every meta tag whose http-equiv is refresh is removed from the
// document before the policy is prefixed, in any attribute order, quoting or
// case, with or without a closing slash; <base> tags are removed too so the
// directive is belt and the strip is braces. The tag regex walks quoted
// values whole, so a ">" inside an attribute value cannot end the tag early
// and leave the real tag standing.
export const MESSAGE_CSP = '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src data:; style-src \'unsafe-inline\'; font-src data:; form-action \'none\'; base-uri \'none\'">';
const META_TAG = /<meta\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi;
const BASE_TAG = /<base\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi;
const HTTP_EQUIV_REFRESH = /\bhttp-equiv\s*=\s*["']?\s*refresh/i;
export function stripFrameNavigation(src) {
  return src.replace(META_TAG, (tag) => (HTTP_EQUIV_REFRESH.test(tag) ? '' : tag)).replace(BASE_TAG, '');
}
export function withMessageCsp(html) {
  const src = stripFrameNavigation(String(html ?? ''));
  const doctype = src.match(/^\s*<!doctype[^>]*>/i);
  if (doctype) return `<!doctype html>${MESSAGE_CSP}${src.slice(doctype[0].length)}`;
  return `${MESSAGE_CSP}${src}`;
}

export function MessageSheet({ id, onClose }) {
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setRes(null); setError(null);
    getV3Message(id).then((r) => { if (alive) setRes(r); }).catch((e) => { if (alive) setError(e?.code || e?.message || 'read failed'); });
    return () => { alive = false; };
  }, [id]);
  const m = res?.data?.message || null;
  const h = m?.headers || {};
  return (
    <Sheet open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:w-3/4 sm:max-w-2xl" data-message-sheet={id || 'none'}>
        <SheetHeader>
          <SheetTitle>{m ? (m.subject || `${word(CHANNEL_WORDS, m.channel)} ${word(DIRECTION_WORDS, m.direction)}`) : 'Message'}</SheetTitle>
          <SheetDescription>{m ? <>{m.at ? formatDateTime(m.at) : NOT_SENT_YET} · {word(CHANNEL_WORDS, m.channel)} · {word(DIRECTION_WORDS, m.direction)}{m.template_slug ? ` · ${m.template_slug}` : ''}{m.provider ? ` · ${m.provider}` : ''}</> : (error ? word(CONVERSATION_ERROR_WORDS, error) : 'reading')}</SheetDescription>
        </SheetHeader>
        {res?.source === 'fixture' ? <p className="text-xs text-warning" data-fixture-badge>{FIXTURE_BADGE}</p> : null}
        {error ? <p className="text-sm text-danger" data-message-error={error}>{word(CONVERSATION_ERROR_WORDS, error)}</p> : null}
        {m ? (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap items-center gap-2"><StatusBadge status={m.status} />{m.status_at ? <span className="text-xs text-text-dim">{formatDateTime(m.status_at)}</span> : null}<Signals signals={m.signals} /></div>
            <dl className="grid grid-cols-[72px_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs" data-message-headers>
              <dt className="text-text-muted">From</dt><dd className="break-all text-text">{h.from || m.from || NOT_RECORDED}</dd>
              <dt className="text-text-muted">To</dt><dd className="break-all text-text">{(h.to || m.to || []).join(', ') || NOT_RECORDED}</dd>
              {h.cc?.length ? <><dt className="text-text-muted">Cc</dt><dd className="break-all text-text">{h.cc.join(', ')}</dd></> : null}
              {h.bcc?.length ? <><dt className="text-text-muted">Bcc</dt><dd className="break-all text-text">{h.bcc.join(', ')}</dd></> : null}
              {h.reply_to ? <><dt className="text-text-muted">Reply to</dt><dd className="break-all font-mono text-text">{h.reply_to}</dd></> : null}
              {m.match ? <><dt className="text-text-muted">Match</dt><dd className="text-text">{word(MATCH_WORDS, m.match.status)}{m.match.by ? ` ${word(MATCHED_BY_WORDS, m.match.by)}` : ''}</dd></> : null}
            </dl>
            {m.body_html
              ? <iframe title="Message as sent" sandbox="" referrerPolicy="no-referrer" srcDoc={withMessageCsp(m.body_html)} className="h-[50vh] w-full rounded-card border border-border bg-white" data-message-html />
              : <pre className="whitespace-pre-wrap rounded-card border border-border bg-bg p-3 font-sans text-sm text-text" data-message-text>{m.body_text || NOT_RECORDED}</pre>}
            {Array.isArray(m.attachments) && m.attachments.length ? (
              <ul className="space-y-0.5 text-xs" data-message-attachments>
                {m.attachments.map((a) => <li key={a.document_id} className="flex items-center gap-1 text-text"><Paperclip size={11} strokeWidth={1.5} /> {a.filename || a.document_id}<span className="text-text-dim">{a.class}</span></li>)}
              </ul>
            ) : null}
            <div data-message-events>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Events</div>
              {Array.isArray(m.events) && m.events.length ? (
                <ul className="mt-1 space-y-0.5 font-mono text-xs text-text-muted">
                  {m.events.map((e, i) => <li key={`${e.type}-${e.at}-${i}`}>{e.type} · {e.at ? formatDateTime(e.at) : NOT_RECORDED}{e.link ? <span className="ml-2 break-all text-text-dim">{e.link}</span> : null}</li>)}
                </ul>
              ) : <p className="text-xs italic text-text-dim">none recorded</p>}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

export function MessageList({ messages = [], empty = NO_MESSAGES }) {
  const [openId, setOpenId] = useState(null);
  return (
    <div data-message-list>
      {messages.length === 0 ? <p className="py-2 text-xs italic text-text-dim">{empty}</p> : messages.map((m) => <MessageRow key={m.id} m={m} onOpen={setOpenId} />)}
      <MessageSheet id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

// The fold under one term (TermBlock): the payload's entry for this term by
// its policy_id, behind a disclosure that says the count.
export function TermCommunications({ t, conversation }) {
  const [open, setOpen] = useState(false);
  const data = conversation?.data || null;
  const entry = data ? (data.terms || []).find((x) => x.policy_id === t.id) || null : null;
  const n = entry ? (entry.messages || []).length : null;
  return (
    <>
      <FactLine label="Conversation" value={
        conversation?.source === NOT_CONNECTED ? <span className="text-sm text-text-dim" data-not-connected="mail">{MAIL_NOT_CONNECTED}</span>
          : conversation?.error ? <span className="text-sm text-danger" data-conversation-error={conversation.error}>{word(CONVERSATION_ERROR_WORDS, conversation.error)}</span>
          : !data ? <span className="text-sm text-text-dim" data-state-word>{NOT_READ_YET}</span>
          : <button type="button" className="inline-flex min-h-[36px] items-center text-sm text-text underline-offset-2 hover:underline" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-communications-toggle={t.id}>{n == null ? 'term not in the read' : n === 0 ? 'none' : `${n} ${n === 1 ? 'message' : 'messages'}`} {open ? '▾' : '▸'}</button>
      } />
      {open && entry ? <div className="border-b border-border pb-2" data-term-communications={t.id}><MessageList messages={entry.messages || []} /></div> : null}
    </>
  );
}

export default function CommunicationsSection({ conversation, terms = [] }) {
  const data = conversation?.data || null;
  const counts = data?.counts || null;
  const termName = (e) => e.label || terms.find((t) => t.id === e.policy_id)?.name || NOT_RECORDED;
  return (
    <section className="rounded-card border border-border bg-surface" data-conversation data-source={conversation?.source || 'none'}>
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-2.5">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Conversation</h2>
        <span className="text-xs text-text-dim">Email and text, one timeline</span>
        {counts ? <span className="font-mono text-[10px] text-text-dim" data-conversation-counts>{counts.messages} messages · {counts.unattributed} unattributed{counts.unmatched_inbound ? ` · ${counts.unmatched_inbound} unmatched inbound` : ''}</span> : null}
        {conversation?.source === 'fixture' ? <span className="rounded-pill bg-warning px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" data-fixture-badge>{FIXTURE_BADGE}</span> : null}
        {data?.read_at ? <span className="ml-auto text-[11px] text-text-dim">read {formatDateTime(data.read_at)}</span> : null}
      </header>
      <div className="px-4">
        {conversation?.error ? (
          <p className="flex flex-wrap items-center gap-3 py-3 text-sm text-danger" data-conversation-error={conversation.error}>
            {word(CONVERSATION_ERROR_WORDS, conversation.error)}
            {conversation.reload ? <button type="button" onClick={conversation.reload} className="inline-flex min-h-[36px] items-center text-sm text-text-muted underline-offset-2 hover:underline">Try again</button> : null}
          </p>
        ) : null}
        {conversation?.source === NOT_CONNECTED ? <p className="py-3 text-sm text-text-dim" data-not-connected="mail">{MAIL_NOT_CONNECTED}</p> : null}
        {!data && !conversation?.error && conversation?.source !== NOT_CONNECTED ? <p className="py-3 text-xs italic text-text-dim" data-state-word>{conversation?.loading ? 'reading' : NOT_READ_YET}</p> : null}
        {data ? (data.terms || []).map((e) => (
          <div key={e.policy_id || e.crm_deal_id} className="py-2" data-conversation-term={e.policy_id}>
            <div className="flex items-baseline gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">{termName(e)}<span className="font-mono normal-case tracking-normal">{(e.messages || []).length}</span></div>
            <MessageList messages={e.messages || []} />
          </div>
        )) : null}
        {data && (data.unattributed || []).length ? (
          <div className="py-2" data-conversation-unattributed>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">{UNATTRIBUTED_MESSAGES}</div>
            <MessageList messages={data.unattributed} />
          </div>
        ) : null}
        {data?.has_more ? <p className="py-2 text-xs italic text-text-dim" data-has-more>{EARLIER_NOT_LOADED}</p> : null}
      </div>
    </section>
  );
}
